import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, Typography } from '@mui/material';
import tenantAdminTheme from './theme';
import { authAPI } from './services/api';

import TenantAdminLayout from './components/TenantAdminLayout';
import LoginPage from './pages/LoginPage';
import SettingsPage from './pages/SettingsPage';
import UsersPage from './pages/UsersPage';

// Chat identity comes from the URL path: /:tenantId/:scriptHash/...
// (react-router v6 has no inline param regexes; format is validated by the
// path parser in services/api.js and by the backend route guards.)

// Gate protected routes: require a token AND a stored membership role allowed to
// use the panel (OWNER/ADMIN). Otherwise bounce to this chat's login.
const ProtectedRoute = ({ children }) => {
  const { tenantId, scriptHash } = useParams();
  if (!authAPI.isAuthenticated()) {
    return <Navigate to={`/${tenantId}/${scriptHash}/login`} replace />;
  }
  return children;
};

// Public (login) route: if already authenticated for the panel, skip to settings.
const PublicRoute = ({ children }) => {
  const { tenantId, scriptHash } = useParams();
  if (authAPI.isAuthenticated()) {
    return <Navigate to={`/${tenantId}/${scriptHash}/settings`} replace />;
  }
  return children;
};

// Shown for a bare/unknown path with no chat context.
const NoChat = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      textAlign: 'center',
      px: 2,
    }}
  >
    <Typography variant="h5" gutterBottom>
      Chat Admin
    </Typography>
    <Typography variant="body1" color="textSecondary" style={{ maxWidth: 480 }}>
      Open your chat admin link to sign in. It looks like
      <br />
      <code>/your-chat-id/your-script-hash/login</code>.
    </Typography>
  </Box>
);

function App() {
  return (
    <ThemeProvider theme={tenantAdminTheme}>
      <CssBaseline />
      <Router>
        <Routes>
          <Route
            path="/:tenantId/:scriptHash/login"
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            }
          />
          <Route
            path="/:tenantId/:scriptHash"
            element={
              <ProtectedRoute>
                <TenantAdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="settings" replace />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="users" element={<UsersPage />} />
          </Route>

          <Route path="*" element={<NoChat />} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
